/**
 * OverviewNav — cross-case navigator. Surfaces *cases* under their full
 * topology: **always** grouped by Client (rendered discreetly — the case
 * is the focus, the client is critical-but-subordinate context), with a
 * Matter folder introduced under a client only when that client spans
 * more than one matter (see `case-overview-tree.ts`). Replaces the
 * always-three-tier `WorkspaceTree` (operator 2026-07-06).
 *
 * Data: one `fetchCases()` call drives the whole structure (cases carry
 * clientId/matterId). Group display names resolve lazily — client names
 * from the cached `useClients()`, matter names from cached
 * `useMatters(clientId)` — falling back to ids/matterType until loaded,
 * so the tree never blocks on a name fan-out.
 *
 * Rendered in two roles (via the `compact` prop):
 *   1. Full-pane on `/home` / `/workspace` — the historic cross-case
 *      landing surface. `compact:false` (default) keeps the
 *      CASES/CLIENTS heading and full padding.
 *   2. **Bottom pane of the split SidePanel** (`compact:true`) — the
 *      always-visible case navigator inside `<CaseNavigator/>`, sits
 *      below the case-workspace file tree so the lawyer can switch
 *      case → case (or case → Global) from any workspace-adjacent
 *      route. Prepends a "Global" pseudo-row (⌂), suppresses the
 *      heading, tightens padding. See
 *      `lexide/src/components/shell/CaseNavigator.tsx` for the
 *      split-pane composition (operator brief 2026-07-01).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useCaseUpdatedStream, useClientUpdatedStream } from "@/hooks/useScopeUpdatedStream";
import {
  AlertCircle,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderInput,
  FolderOpen,
  FolderTree,
  Globe,
  Inbox,
  Loader2,
  MoreHorizontal,
  Trash2,
  User,
  Users,
} from "lucide-react";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { DestructiveCaseDialog } from "@/components/cases/DestructiveCaseDialog";
import { RowContextMenu } from "@/components/shell/RowContextMenu";
import { useClients } from "@/hooks/useClients";
import { useMatters } from "@/hooks/useMatters";
import { recordRecentWork } from "@/hooks/useRecentWork";
import { fetchCases, type CaseListItem, type ClientListItem, type MatterListItem } from "@/lib/lexserver-client";
import { clientDisplayLabel } from "@/lib/client-shortname";
import { resolveDisplayName } from "@/lib/entity-display";
import { useLanguageStore } from "@/stores/useLanguageStore";
import { useFormatters } from "@/lib/formatters";
import { ClientAttributesDialog } from "./ClientAttributesDialog";
import { MatterAttributesDialog } from "./MatterAttributesDialog";
import { AssignCaseDialog } from "@/components/context-pane/AssignCaseDialog";
import {
  groupCasesForOverview,
  type GroupNode,
  type NavNode,
} from "@/lib/workspace/case-overview-tree";
import { useChatUnreadStore } from "@/stores/useChatUnreadStore";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { cn } from "@/lib/utils";
import type {
  CaseWorkspaceTreeResult,
  WorkspaceTreeFolder,
} from "@/types/gateway";

export interface OverviewNavProps {
  /** When true, render for the bottom-of-sidebar "case navigator"
   *  role (operator brief 2026-07-01):
   *   - Prepends a "Global" pseudo-row that clears the active
   *     selection + navigates to `/chat` (the org-scope surface).
   *   - Suppresses the CASES/CLIENTS heading (the wrapping pane
   *     provides its own semantics).
   *   - Tightens padding so the compact list packs more rows into the
   *     ~25% default share of the SidePanel.
   *  Off = the historic full-pane rendering (used by the home /
   *  workspace routes where OverviewNav is the ONLY thing in the
   *  sidebar). The `min-h-0` + `overflow-auto` scroll-container shape
   *  is applied in BOTH modes — SplitRow-safe by default. */
  compact?: boolean;
}

export function OverviewNav({ compact = false }: OverviewNavProps = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: cases, isLoading, error } = useQuery<CaseListItem[], Error>({
    queryKey: ["overview-cases"],
    queryFn: fetchCases,
    staleTime: 60_000,
    retry: 2,
  });

  // Live-refresh the cross-case list whenever the runtime broadcasts a case
  // lifecycle change — so a case created via the New-Case button OR the
  // concierge (`lx_create_case`) appears here without a manual reload. Fires
  // org-wide (no scope filter — this pane spans all clients/matters). Depends
  // on the runtime emitting `case.updated{created}` on BOTH paths (the gateway
  // create handler broadcasts; the concierge tool path is a runtime follow-up).
  useCaseUpdatedStream((signal) => {
    void queryClient.invalidateQueries({ queryKey: ["overview-cases"] });
    // Keep the flat `["cases"]` list (CasesListLayout / Command Palette / case
    // switcher — a SEPARATE fetchCases() key) in sync with the navigator for EVERY
    // case.updated (concierge lx_create_case, reassign, reslug).
    void queryClient.invalidateQueries({ queryKey: ["cases"] });
    // A chain-CREATE (dialog OR concierge `lx_create_case`) may auto-add a client
    // and/or matter; the runtime broadcasts only `case.updated` (not
    // `client.updated`) on that path, so refresh the client + matter caches here
    // too — else the new group headers render their raw id/slug until reload
    // (handback 2026-07-07 §6). This is the SOLE refresh trigger for the concierge
    // path (which has no dialog `onCreated`); the dialog path is also covered
    // deterministically in `NewCaseDialog.handleCreate`. Gated on `created` — a
    // status-change / rename can't introduce a new client/matter.
    if (signal.kind === "created") {
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      void queryClient.invalidateQueries({ queryKey: ["matters"] });
    } else if (signal.kind === "reassigned") {
      // A reassign (`case.assign`) moves the case under an EXISTING client/matter
      // (id-based, no new entity), but the destination matter's name may not be
      // cached yet — refresh that client's matters so the new group header resolves
      // (not its raw id). Covers ANY initiator (this dialog, another client's live
      // view, or a future concierge `lx_assign_case`), not just the local dialog.
      const destClientId = typeof signal.clientId === "string" ? signal.clientId : undefined;
      void queryClient.invalidateQueries({
        queryKey: destClientId ? ["matters", destClientId] : ["matters"],
      });
    }
  });
  // Live-refresh the client rows (discreet `@alias` / shortname) when a client is
  // renamed or re-aliased — from the attributes dialog here OR the concierge
  // (`lx_update_client` fires `client.updated`).
  useClientUpdatedStream(() => {
    void queryClient.invalidateQueries({ queryKey: ["clients"] });
    // A client DELETE (the ruled hard-delete) unassigns its cases in ONE bulk
    // registry op — the runtime broadcasts only client.updated{deleted}, no
    // per-case case.updated. Refresh the case trees so the group dissolves
    // into «Não atribuídos» for every viewer, not just the deleting one.
    void queryClient.invalidateQueries({ queryKey: ["overview-cases"] });
    void queryClient.invalidateQueries({ queryKey: ["cases"] });
  });
  const { data: clients } = useClients();
  // Active scope → smart-default seeds for the global "New case" affordance
  // (operator 2026-07-06: if a client/matter is already in context, pre-fill).
  // The client whose attributes dialog is open (operator 2026-07-06: a client-row
  // click opens a view/edit dialog instead of navigating to the detail page; the
  // dialog carries an "Open full page" link there).
  const [dialogClient, setDialogClient] = useState<ClientListItem | null>(null);
  // N2: the matter twin — { clientId, matter } for MatterAttributesDialog.
  const [dialogMatter, setDialogMatter] = useState<{
    clientId: string;
    matter: MatterListItem;
  } | null>(null);

  const clientsById = useMemo(() => {
    const map = new Map<string, ClientListItem>();
    for (const c of clients ?? []) map.set(c.clientId, c);
    return map;
  }, [clients]);

  const nodes = useMemo(() => groupCasesForOverview(cases ?? []), [cases]);

  // Adaptive heading: when any case carries a client the pane is a client
  // tree → "CLIENTS"; only when every case is client-less does it list cases
  // directly → "CASES" (the client level is otherwise always present).
  const hasClientGroups = nodes.some(
    (n) => n.kind === "group" && n.groupBy === "client",
  );

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-auto",
        compact ? "p-2" : "p-3",
      )}
      data-testid="workspace-tree-sidebar"
      data-compact={compact ? "true" : undefined}
    >
      {/* Create affordances (New case / New client) live in the CaseNavigator
          reverse title bar (operator 2026-07-08 — blended into the CASOS
          headline), not here. */}
      {!compact && (
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {hasClientGroups ? (
            <Users size={14} className="text-slate-400" />
          ) : (
            <FolderTree size={14} className="text-slate-400" />
          )}
          <span>
            {hasClientGroups
              ? t("workspaceTree.clientsHeading")
              : t("workspaceTree.casesHeading")}
          </span>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 py-3 text-xs text-slate-400">
          <Loader2 size={14} className="animate-spin" />
          {t("workspaceTree.casesLoading")}
        </div>
      )}

      {error && (
        <div
          className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
          data-testid="workspace-tree-error"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{t("workspaceTree.failedLoadCasesSidebar", { message: error.message })}</span>
        </div>
      )}

      {/* Global pseudo-row — always the first entry in the compact
          case-navigator role (operator brief 2026-07-01). Clicking
          clears the active selection + navigates to /chat, which flips
          the top workspace pane to org scope automatically via
          `useEffectiveScope`. Hidden in non-compact mode where the
          Home button already covers this affordance. */}
      {compact && <GlobalRow />}

      {cases && cases.length === 0 && (
        <div
          className="flex flex-col items-center py-6 text-center text-xs text-slate-400 dark:text-slate-500"
          data-testid="workspace-tree-empty"
        >
          <FolderTree size={24} strokeWidth={1.5} />
          <p className="mt-2">{t("workspaceTree.noCasesYet")}</p>
        </div>
      )}

      {cases && cases.length > 0 && (
        <ul className="flex flex-col" data-testid="workspace-tree">
          <NodeList
            nodes={nodes}
            clientsById={clientsById}
            onOpenClient={setDialogClient}
            onOpenMatter={(clientId, matter) => setDialogMatter({ clientId, matter })}
          />
        </ul>
      )}

      {dialogClient && (
        <ClientAttributesDialog client={dialogClient} onClose={() => setDialogClient(null)} />
      )}
      {dialogMatter && (
        <MatterAttributesDialog
          clientId={dialogMatter.clientId}
          matter={dialogMatter.matter}
          onClose={() => setDialogMatter(null)}
        />
      )}
    </div>
  );
}

/** Always-first row in the case navigator's compact mode — a doorway
 *  back to the per-user "Global" (org-scope) chat surface. Highlighted
 *  when no case is selected. Clicking clears the selection + navigates
 *  to /chat; the top workspace pane re-renders in org scope via the
 *  existing `useEffectiveScope` hook. */
function GlobalRow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clearSelection = useActiveContextStore((s) => s.clear);
  // "Active" = no client/matter/case selection at all (org/global scope).
  const selection = useActiveContextStore((s) => s.selection);
  const isActive = selection == null;

  const orgRecency = useOrgWorkspaceRecency();
  const fmt = useFormatters();

  function handleClick() {
    clearSelection();
    navigate("/chat");
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="case-navigator-global-row"
      data-active={isActive ? "true" : undefined}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "mb-1 flex min-h-[22px] w-full items-center gap-2 rounded-md px-1.5 text-left text-[13px] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40",
        isActive
          ? "bg-slate-200 font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100"
          : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800",
      )}
    >
      <Globe
        size={14}
        className={isActive ? "text-slate-700 dark:text-slate-200" : "text-slate-400"}
      />
      <span className="flex-1 truncate">{t("workspace.caseNavigator.globalRow")}</span>
      {/* Global's own "last updated" (operator 2026-07-27) — so the row is not
          the one ragged edge in a list where every other row carries a stamp.
          It is a REAL value: the newest `modifiedAt` in the org workspace tree.
          It renders ONLY when that data is actually present; nothing is
          synthesised and no case timestamp is borrowed. See
          `useOrgWorkspaceRecency`. */}
      {orgRecency && (
        <span
          data-testid="workspace-global-recency"
          className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500"
          title={t("workspace.recency.workspaceUpdated", {
            when: fmt.date(orgRecency, { dateStyle: "medium", timeStyle: "short" }),
          })}
        >
          {fmt.relativeTime(orgRecency, undefined, "narrow")}
        </span>
      )}
    </button>
  );
}

/**
 * Newest `modifiedAt` anywhere in a workspace tree, as an ISO string — or
 * `null` when the tree carries no timestamps at all.
 *
 * Folders are not consulted: the runtime stamps `modifiedAt` on FILES
 * (`WorkspaceTreeFile`), and a folder's own mtime changes on unrelated
 * bookkeeping, which would make the value drift without anything the lawyer
 * would call an update.
 *
 * Exported for the colocated unit test.
 */
export function newestWorkspaceMtime(folder: WorkspaceTreeFolder): string | null {
  let newest: string | null = null;
  const visit = (f: WorkspaceTreeFolder) => {
    for (const file of f.files) {
      const m = file.modifiedAt;
      // ISO-8601 UTC strings compare correctly lexicographically, so no Date
      // allocation per file.
      if (m && (newest === null || m > newest)) newest = m;
    }
    for (const sub of f.folders) visit(sub);
  };
  visit(folder);
  return newest;
}

/**
 * "Last updated" for the GLOBAL (org) workspace row.
 *
 * Deliberately reuses the EXACT React-Query key `useCaseWorkspaceTree` uses for
 * org scope (`["case-workspace-tree", "org"]`), so this shares that cache rather
 * than issuing a second walk: when the lawyer is already in Global scope
 * `CaseFileNav` has populated it, and when they are inside a case this warms it
 * for the switch back.
 *
 * ⚠ Returns `null` rather than a fallback when the tree is absent, erroring, or
 * carries no mtimes. That is deliberate and load-bearing: `lastActivityAt` on
 * CASES is a documented phantom field whose `??` fallback silently presented a
 * metadata date as activity (`lexide-layout-plan.md` §3.2). A second synthesised
 * timestamp on this surface would repeat exactly that mistake.
 */
function useOrgWorkspaceRecency(): string | null {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const { data } = useQuery<CaseWorkspaceTreeResult, Error>({
    queryKey: ["case-workspace-tree", "org"],
    queryFn: () => rpc!.caseWorkspaceTreeOrg(),
    enabled: Boolean(rpc) && connectionState === "connected",
    staleTime: 30_000,
    retry: 1,
  });
  return useMemo(() => (data ? newestWorkspaceMtime(data.root) : null), [data]);
}

interface NodeListProps {
  nodes: NavNode[];
  clientsById: Map<string, ClientListItem>;
  /** Open the client attributes dialog for a client row (threaded from OverviewNav). */
  onOpenClient: (client: ClientListItem) => void;
  onOpenMatter: (clientId: string, matter: MatterListItem) => void;
  /** True when these nodes live inside the Unassigned bucket — surfaces the
   *  per-row "Assign" affordance on the leaf CaseRows. */
  unassigned?: boolean;
}

function NodeList({ nodes, clientsById, onOpenClient, onOpenMatter, unassigned }: NodeListProps) {
  return (
    <>
      {nodes.map((node) =>
        node.kind === "case" ? (
          <CaseRow
            key={node.caseItem.caseId}
            caseItem={node.caseItem}
            clientsById={clientsById}
            unassigned={unassigned}
          />
        ) : node.groupBy === "client" ? (
          <ClientGroup key={`client:${node.id}`} node={node} clientsById={clientsById} onOpenClient={onOpenClient} onOpenMatter={onOpenMatter} />
        ) : node.groupBy === "unassigned" ? (
          <UnassignedGroup key="unassigned" node={node} clientsById={clientsById} onOpenClient={onOpenClient} onOpenMatter={onOpenMatter} />
        ) : (
          <MatterGroup key={`matter:${node.id}`} node={node} clientsById={clientsById} onOpenClient={onOpenClient} onOpenMatter={onOpenMatter} />
        ),
      )}
    </>
  );
}

/** Count case leaves anywhere beneath a node (for the row's badge). */
function countCases(nodes: NavNode[]): number {
  let n = 0;
  for (const node of nodes) {
    if (node.kind === "case") n += 1;
    else n += countCases(node.children);
  }
  return n;
}

function ClientGroup({
  node,
  clientsById,
  onOpenClient,
  onOpenMatter,
}: {
  node: GroupNode;
  clientsById: Map<string, ClientListItem>;
  onOpenClient: (client: ClientListItem) => void;
  onOpenMatter: (clientId: string, matter: MatterListItem) => void;
}) {
  const { t } = useTranslation();
  // Default OPEN — the operator wants the topology + cases visible at a glance;
  // the client is a discreet grouping header, the cases are the focus.
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const selectClient = useActiveContextStore((s) => s.selectClient);
  const client = clientsById.get(node.id);
  // Discreet, compact client label (operator 2026-07-06) — the curated `@alias`
  // handle when set, else a shortname derived from the name. The client is
  // critical context but must not compete with the case for attention.
  const short = clientDisplayLabel(client, node.id);
  const count = countCases(node.children);

  // Click opens the attributes dialog (view full info + edit name/alias/
  // status/delete). Before the row is cached there is nothing to show yet —
  // select the scope and let the next click open the dialog (N3 removed the
  // detail-route fallback along with the page).
  function handleOpen() {
    if (client) {
      onOpenClient(client);
      return;
    }
    selectClient(node.id);
  }

  return (
    // Breathing room BETWEEN clients (operator 2026-07-27). With one header per
    // client the list is a dense stripe of alternating group/case rows; a gap at
    // the client boundary is what lets the eye find where one client's cases end.
    <li data-testid="workspace-client-node" className="mt-2 first:mt-0">
      {/* Discreet client header: small · thin · light — SUBORDINATE to the cases
          (the original 2026-07-06 intent, restored 2026-07-27). An earlier draft
          of this pass gave every group the workspace tree's uppercase section
          caption; that reads fine on the tree, which has ~7 FIXED zone headings,
          but the client count here is UNBOUNDED — one uppercase caption per
          client turned the panel into a wall of capitals and promoted the client
          above the case, which is backwards. The case is the focus; the client
          is context. */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? t("workspaceTree.collapseClient", { name: short }) : t("workspaceTree.expandClient", { name: short })}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400"
        >
          {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button
          type="button"
          onClick={handleOpen}
          data-testid="workspace-client-name"
          title={client?.name ?? node.id}
          className="flex min-h-[22px] flex-1 items-center gap-1 rounded px-1 text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:hover:bg-slate-800"
        >
          {client?.isPerson ? (
            <User size={11} className="shrink-0 text-slate-400 dark:text-slate-500" />
          ) : (
            <Users size={11} className="shrink-0 text-slate-400 dark:text-slate-500" />
          )}
          {/* C1/DC-2: ONE style for every grouping row — client, matter and
              unassigned are the same structural thing and had three different
              looks (10px font-light slate / same / 11px medium uppercase amber).
              Unified here, but deliberately NOT the tree's uppercase caption:
              see the note on the client `<li>` for why uppercase does not scale
              to an unbounded list. Lighter than the case rows on purpose —
              the client is context, the case is the focus. */}
          <span className="min-w-0 truncate text-[11px] font-medium text-slate-400 dark:text-slate-500">
            {short}
          </span>
          {/* GROUP COUNT — bound to the name, not the row's right edge (operator
              2026-07-30, matching the file tree). The label span used to carry
              `flex-1`, which grew it across the row and left the number stranded
              at the far edge, a long way from short client names.
              `shrink-0` is load-bearing: a long name must ellipsize, the count
              must not vanish.
              ⚠ This is the COUNT only. The recency stamps
              (`workspace-global-recency` / `workspace-case-recency`) keep their
              `flex-1`-driven right alignment on purpose — they form the
              right-hand stamp column this list is built around (see the note
              above `workspace-global-recency`). No row carries both, so the two
              treatments never collide. */}
          <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">{count}</span>
        </button>
      </div>
      {expanded && (
        <ul className="ml-[7px] flex flex-col border-l border-cream-200/70 pl-2 dark:border-coffee-700/50">
          <NodeList nodes={node.children} clientsById={clientsById} onOpenClient={onOpenClient} onOpenMatter={onOpenMatter} />
        </ul>
      )}
    </li>
  );
}

/** The client-less "assignment-pending" bucket — a dedicated, default-expanded
 *  group emitted LAST (see `groupCasesForOverview`). Reads as "needs attention"
 *  (warmer amber accent, Inbox icon, static i18n label) rather than the discreet
 *  slate of client headers; its leaf rows carry the per-row "Assign" affordance. */
function UnassignedGroup({
  node,
  clientsById,
  onOpenClient,
  onOpenMatter,
}: {
  node: GroupNode;
  clientsById: Map<string, ClientListItem>;
  onOpenClient: (client: ClientListItem) => void;
  onOpenMatter: (clientId: string, matter: MatterListItem) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const label = t("workspaceTree.unassignedHeading");
  const count = countCases(node.children);

  return (
    // Same client-boundary spacing — this group sits at the same level.
    <li data-testid="workspace-unassigned-node" className="mt-2 first:mt-0">
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? t("workspaceTree.collapseClient", { name: label }) : t("workspaceTree.expandClient", { name: label })}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <div
          data-testid="workspace-unassigned-header"
          title={label}
          className="flex min-h-[22px] flex-1 items-center gap-1 rounded px-1 text-left"
        >
          {/* C1: the amber is GONE from this group. It was the loudest remaining
              gold/yellow in the SidePanel and sat ~200px below a tree that has
              none. "Unassigned" is already self-describing in words and the
              count carries the urgency — the colour was re-stating both. */}
          <Inbox size={11} className="shrink-0 text-slate-400 dark:text-slate-500" />
          <span className="min-w-0 truncate text-[11px] font-medium text-slate-400 dark:text-slate-500">
            {label}
          </span>
          {/* GROUP COUNT — bound to the name, not the row's right edge (operator
              2026-07-30, matching the file tree). The label span used to carry
              `flex-1`, which grew it across the row and left the number stranded
              at the far edge, a long way from short client names.
              `shrink-0` is load-bearing: a long name must ellipsize, the count
              must not vanish.
              ⚠ This is the COUNT only. The recency stamps
              (`workspace-global-recency` / `workspace-case-recency`) keep their
              `flex-1`-driven right alignment on purpose — they form the
              right-hand stamp column this list is built around (see the note
              above `workspace-global-recency`). No row carries both, so the two
              treatments never collide. */}
          <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">{count}</span>
        </div>
      </div>
      {expanded && (
        <ul className="ml-[7px] flex flex-col border-l border-cream-200/70 pl-2 dark:border-coffee-700/50">
          <NodeList nodes={node.children} clientsById={clientsById} onOpenClient={onOpenClient} onOpenMatter={onOpenMatter} unassigned />
        </ul>
      )}
    </li>
  );
}

function MatterGroup({
  node,
  clientsById,
  onOpenClient,
  onOpenMatter,
}: {
  node: GroupNode;
  clientsById: Map<string, ClientListItem>;
  onOpenClient: (client: ClientListItem) => void;
  onOpenMatter: (clientId: string, matter: MatterListItem) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const selectMatter = useActiveContextStore((s) => s.selectMatter);
  // Resolve the matter's display name from the (cached) matter list of
  // its parent client. Enabled only when we know the clientId; falls
  // back to the cases' matterType, then the matterId, until loaded.
  const { data: matters } = useMatters(node.clientId);
  const lang = useLanguageStore((s) => s.lang);
  // Prefer the i18n displayNames (entity-identity Slice 1), fall back to the
  // legacy `name`, then the cases' matterType, then the matterId — until loaded.
  const matter = matters?.find((m) => m.matterId === node.id);
  const matterName = resolveDisplayName(matter, lang) || firstMatterType(node) || node.id;
  const count = countCases(node.children);

  // N2 (plan 1182f2034, Journey 3): the name click SELECTS the scope and
  // expands — the full-page exit to /clients/:c/matters/:m is GONE (it was the
  // audit's worst-ranked flow exit). Managing the matter (rename, + Novo caso)
  // lives behind the ⋯ affordance → MatterAttributesDialog.
  function handleOpen() {
    if (node.clientId) selectMatter(node.clientId, node.id);
    setExpanded(true);
  }

  function handleManage(e: React.MouseEvent) {
    e.stopPropagation();
    if (node.clientId && matter) onOpenMatter(node.clientId, matter);
  }

  return (
    <li data-testid="workspace-matter-node">
      {/* Discreet matter header — only shown when a client spans >1 matter. */}
      <div className="group/matterrow flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? t("workspaceTree.collapseClient", { name: matterName }) : t("workspaceTree.expandClient", { name: matterName })}
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400"
        >
          {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button
          type="button"
          onClick={handleOpen}
          title={matterName}
          className="flex min-h-[22px] flex-1 items-center gap-1 rounded px-1 text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:hover:bg-slate-800"
        >
          <Briefcase size={11} className="shrink-0 text-slate-400 dark:text-slate-500" />
          {/* Same caption as the client + unassigned groups — see DC-2. */}
          <span className="min-w-0 truncate text-[11px] font-medium text-slate-400 dark:text-slate-500">
            {matterName}
          </span>
          {/* GROUP COUNT — bound to the name, not the row's right edge (operator
              2026-07-30, matching the file tree). The label span used to carry
              `flex-1`, which grew it across the row and left the number stranded
              at the far edge, a long way from short client names.
              `shrink-0` is load-bearing: a long name must ellipsize, the count
              must not vanish.
              ⚠ This is the COUNT only. The recency stamps
              (`workspace-global-recency` / `workspace-case-recency`) keep their
              `flex-1`-driven right alignment on purpose — they form the
              right-hand stamp column this list is built around (see the note
              above `workspace-global-recency`). No row carries both, so the two
              treatments never collide. */}
          <span className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500">{count}</span>
        </button>
        {/* ⋯ manage — opens MatterAttributesDialog (rename · + Novo caso).
            Rendered only once the matter row is cached (the dialog needs it);
            hover/focus-revealed to keep the row quiet. */}
        {matter && (
          <button
            type="button"
            data-testid="matter-manage"
            onClick={handleManage}
            title={t("workspaceTree.manageMatter", { name: matterName })}
            aria-label={t("workspaceTree.manageMatter", { name: matterName })}
            className="flex h-4 w-5 shrink-0 items-center justify-center rounded text-slate-300 opacity-0 transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 group-hover/matterrow:opacity-100 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400"
          >
            <MoreHorizontal size={12} aria-hidden />
          </button>
        )}
      </div>
      {expanded && (
        <ul className="ml-[7px] flex flex-col border-l border-cream-200/70 pl-2 dark:border-coffee-700/50">
          <NodeList nodes={node.children} clientsById={clientsById} onOpenClient={onOpenClient} onOpenMatter={onOpenMatter} />
        </ul>
      )}
    </li>
  );
}

function firstMatterType(node: GroupNode): string | undefined {
  for (const child of node.children) {
    if (child.kind === "case") return child.caseItem.matterType;
  }
  return undefined;
}

function CaseRow({
  caseItem,
  clientsById,
  unassigned,
}: {
  caseItem: CaseListItem;
  clientsById: Map<string, ClientListItem>;
  /** When true, this row is inside the Unassigned bucket → show the "Assign"
   *  affordance opening the AssignCaseDialog. */
  unassigned?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = useLanguageStore((s) => s.lang);
  const fmt = useFormatters();
  const selectCase = useActiveContextStore((s) => s.selectCase);
  const activeCaseId = useActiveContextStore((s) => s.selection?.caseId ?? null);
  const isActive = activeCaseId === caseItem.caseId;
  const unreadCount = useChatUnreadStore((s) => s.unreadByCase[caseItem.caseId] ?? 0);
  const rowRef = useRef<HTMLButtonElement>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const { identity } = useAuthIdentity();
  const rpc = useGatewayStore((st: { rpc: unknown }) => st.rpc);
  const queryClient = useQueryClient();
  // Retiring a case is an org-admin action (operator 2026-07-19). The server
  // is authoritative; hiding the control is defense-in-depth.
  const canRetire = identity?.isOrgAdmin ?? false;
  // Route the case title through the entity-identity resolver (fail-soft → title)
  // so it lights up if cases gain i18n displayNames; unchanged for today's rows.
  const caseTitle =
    resolveDisplayName({ name: caseItem.title, slug: caseItem.slug, id: caseItem.caseId }, lang) ||
    caseItem.title;
  // The row's trailing meta shows RECENCY (the sort key), not the raw status.
  //
  // `lastActivityAt` = LAST CONVERSATIONAL ACTIVITY — any message, either
  // direction, any channel (operator ruling 2026-07-27). It became a real
  // lexserver registry field in `77acc2c50`; before that it was declared here
  // and populated by nobody, so this `??` silently rendered a METADATA stamp as
  // recency and the list looked weeks stale on cases worked yesterday.
  //
  // The fallback STAYS, deliberately: `null` now means "no conversation
  // recorded", and until the runtime starts emitting EVERY row is null — so
  // rendering the null state as "sem conversa" today would flip the whole list
  // to an empty-looking column. That is honest but reads as broken, which is a
  // worse trade than a slightly-wrong date.
  //
  // What we DO fix now is the lie in the tooltip: the two cases are no longer
  // presented as the same thing. Hovering says which one you are looking at, so
  // the display is honest in both states and needs no second change when the
  // emit lands.
  const hasConversation = typeof caseItem.lastActivityAt === "string";
  const recency = caseItem.lastActivityAt ?? caseItem.updatedAt;

  // Auto-scroll the active case into view when it becomes active or
  // when the navigator mounts with it already active. Operator brief
  // 2026-07-01: "Case navigator always highlighting AND focusing the
  // selected case." `block: "nearest"` avoids yanking the pane's
  // scroll when the row is already visible.
  useEffect(() => {
    if (!isActive) return;
    // Slight timeout so the compact overflow container has finished
    // laying out (SplitRow ratio-flexbasis paints after mount).
    const tid = window.setTimeout(() => {
      rowRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }, 50);
    return () => window.clearTimeout(tid);
  }, [isActive]);

  function handleOpen() {
    // `null` (NOT "") for an unassigned case — an empty string launders past the
    // downstream `?? "Unassigned"` fallback and renders a blank client segment (L1).
    selectCase(caseItem.clientId ?? null, caseItem.matterId ?? null, caseItem.caseId);
    const clientName = caseItem.clientId ? clientsById.get(caseItem.clientId)?.name : undefined;
    const subtitle = [clientName, caseItem.matterType].filter(Boolean).join(" › ");
    recordRecentWork({
      kind: "case",
      id: caseItem.caseId,
      label: caseItem.title,
      subtitle: subtitle || undefined,
      href: `/cases/${caseItem.caseId}`,
    });
    navigate(`/cases/${caseItem.caseId}`);
  }

  return (
    <li>
      <div className="group/caserow relative flex items-center">
        <button
          ref={rowRef}
          type="button"
          onClick={handleOpen}
          data-testid="workspace-case-row"
          data-case-id={caseItem.caseId}
          data-active={isActive ? "true" : undefined}
          aria-current={isActive ? "page" : undefined}
          onContextMenu={(e) => {
            if (!canRetire && !unassigned) return;
            e.preventDefault();
            setMenuAt({ x: e.clientX, y: e.clientY });
          }}
          className={cn(
            // C2: min-h-[22px], matching the workspace tree's rows exactly. NOT
            // a fixed `h-` — the tree pass proved a fixed height silently
            // overflows any row that gains a second line.
            "flex min-h-[22px] w-full items-center gap-2 rounded-md px-1.5 text-left text-[13px] transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40",
            unassigned && "pr-8",
            isActive
              ? "bg-slate-200 font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100"
              : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800",
          )}
        >
          {/* A case IS a folder (operator 2026-07-27) — and the OPEN folder is
              the "you are here" cue, so the active case reads as opened rather
              than merely highlighted. Same glyph pair + same ink ramp as the
              workspace tree's folders above, which is the point: one vocabulary
              across both panels. */}
          {isActive ? (
            <FolderOpen size={14} className="shrink-0 text-slate-600 dark:text-slate-300" />
          ) : (
            <Folder size={14} className="shrink-0 text-slate-400 dark:text-slate-500" />
          )}
          <span className="flex-1 truncate">{caseTitle}</span>
          {/* P6 item 3 — unread dot: assistant finals landed on this case's
              thread while it was not the open chat surface (⚖ D-8 turns
              arrive up to ~65s after a mission ends, usually after the
              lawyer moved on). Cleared by opening the case. A dot, not a
              number — the claim is "new activity", nothing finer. */}
          {unreadCount > 0 && !isActive && (
            <span
              data-testid="workspace-case-unread"
              role="status"
              aria-label={t("workspace.unreadActivity")}
              title={t("workspace.unreadActivity")}
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500"
            />
          )}
          <span
            data-testid="workspace-case-recency"
            className="shrink-0 text-[10px] tabular-nums text-slate-400 dark:text-slate-500"
            title={
              hasConversation
                ? t("workspace.recency.lastMessage", {
                    when: fmt.date(recency, { dateStyle: "medium", timeStyle: "short" }),
                  })
                : t("workspace.recency.noConversation", {
                    when: fmt.date(recency, { dateStyle: "medium", timeStyle: "short" }),
                  })
            }
          >
            {fmt.relativeTime(recency, undefined, "narrow")}
          </span>
        </button>
        {unassigned && (
          <button
            type="button"
            data-testid="assign-case-trigger"
            onClick={(e) => {
              e.stopPropagation();
              setAssignOpen(true);
            }}
            aria-label={t("workspaceTree.assignCase")}
            title={t("workspaceTree.assignCase")}
            // DC-4: neutral, and revealed on row hover/focus rather than parked
            // permanently beside every unassigned case. Same principle the
            // retire action already follows (see the note below) — an action
            // that lives in the row competes with the row's own content.
            className="absolute right-1 flex h-5 w-5 items-center justify-center rounded-md text-slate-400 opacity-0 transition-opacity transition-colors hover:bg-slate-200 hover:text-slate-700 focus-visible:opacity-100 group-hover/caserow:opacity-100 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <FolderInput size={13} />
          </button>
        )}
      </div>
      {/* Destructive actions live on RIGHT-CLICK, not in the row. A trash
          icon parked beside every case title is both visually loud and an
          invitation to mis-click on the one action that removes a matter
          from the lawyer's view. This is the same move the workspace file
          rows already made (see RowContextMenu's own note). */}
      {menuAt && (
        <RowContextMenu
          x={menuAt.x}
          y={menuAt.y}
          onClose={() => setMenuAt(null)}
          items={[
            ...(unassigned
              ? [{
                  key: "assign",
                  label: t("workspaceTree.assignCase"),
                  Icon: FolderInput,
                  onSelect: () => setAssignOpen(true),
                }]
              : []),
            ...(canRetire
              ? [{
                  key: "retire",
                  label: t("workspaceTree.deleteCase"),
                  Icon: Trash2,
                  onSelect: () => setDeleteOpen(true),
                }]
              : []),
          ]}
        />
      )}
      {deleteOpen && (
        <DestructiveCaseDialog
          target={{ title: caseTitle, handle: caseItem.caseId }}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={async () => {
            if (!rpc) throw new Error("gateway not connected");
            await (rpc as { caseDelete: (p: { caseId: string }) => Promise<unknown> })
              .caseDelete({ caseId: caseItem.caseId });
            setDeleteOpen(false);
            void queryClient.invalidateQueries({ queryKey: ["overview-cases"] });
            void queryClient.invalidateQueries({ queryKey: ["cases"] });
          }}
        />
      )}
      {assignOpen && <AssignCaseDialog caseItem={caseItem} onClose={() => setAssignOpen(false)} />}
    </li>
  );
}
